import { sortBy, uniq } from 'lodash-es'

import {
  getToolkitCardTitle,
  getWellnessToolkitCardAccessibilityLabel,
  TOOLKIT_TO_LIBRARY_TOPIC_ID,
  WELLNESS_GOAL_TO_WELLNESS_TOOLKIT_PROMOTION,
  WELLNESS_GOALS_RANKING,
  WELLNESS_GOALS_WORD_CLOUD_ORDER,
  WFMH_DEFAULT_TOOLKIT_PROMOTION_RANKING,
  WFMH_DEPRIORITIZED_NEURODIVERSITY_COE_PROMOTION_RANKING,
  WFMH_TRIGGERED_TOOLKIT_RANKING,
} from './constants'
import {
  ShortCircuitEligibilityResult,
  TOOLKIT_TYPE,
  ToolkitAvailabilityInputs,
  ToolkitAvailabilityReason,
  ToolkitAvailabilityResult,
  ToolkitShortCircuitConditions,
  ToolkitShortCircuitEligibilityInputs,
  WELLNESS_GOALS_WORD_CLOUD_OPTIONS,
  WELLNESS_TOOLKIT_PROMOTION,
  WellnessToolkitAvailableConfig,
} from './types'
import {
  checkTriggerConditionsFamily,
  checkTriggerConditionsHealthChallenges,
  checkTriggerConditionsManagerHub,
  checkTriggerConditionsNeurodiversity,
  checkTriggerConditionsParent,
  checkTriggerConditionsTrauma,
  checkTriggerConditionsWomensHealth,
} from './wellnessToolkitTriggersUtil'
import { Episode, EpisodeStates } from '../../models/episodes/Episodes'
import { ProgramNames } from '../../models/programs/Programs'
import { RiskFactorsResponse } from '../../models/riskFactors/RiskFactors'
import { TriageSearch, TriageSearchStatus } from '../../models/searchForCare/SearchForCare'
import { CENTER_OF_EXCELLENCE } from '../centerOfExcellence/types'
import { CustomerName } from '../common/constants/customer'
import { COMMON_MIXPANEL_PAGE } from '../common/constants/mixpanelConstants'
import { PRODUCT_TYPE } from '../common/constants/productType'
import { COACHING_PARAMS, PARENTING_PARAMS } from '../common/constants/speculativeConstant'
import { TREATMENT_RECOMMENDATION_TIER } from '../common/constants/treatmentRecommendationsConstants'
import { hasPastSI } from '../highAlert/utils'
import {
  getManagerCoachingRemainingSessions,
  MANAGER_COACHING_SEAT_ELIGIBILITY_STATUS,
} from '../managerCoaching/seatEligibility'
import { isChildSearch } from '../searchForCare/utils'

/**
 * Conditions for when a toolkit is available: https://lyrahealth.atlassian.net/wiki/spaces/EN/pages/3448995850/Wellness+Toolkits+Coaching+Parenting+Family+Hub
 * @param toolkit Toolkit
 * @param isLyraSelect If user is on Lyra Select
 * @param page Where the toolkit link is being shown. Defaults to homebase carousel
 * @returns Config with a list of conditions mapped to if it applies or not to the given toolkit
 */
export const getWellnessToolkitAvailableConfig = ({
  toolkit,
  isLyraSelect,
  page,
  isInternationalLifestyleCoachingEnabled = false,
}: {
  toolkit: TOOLKIT_TYPE
  isLyraSelect: boolean
  page?: COMMON_MIXPANEL_PAGE
  isInternationalLifestyleCoachingEnabled?: boolean
}): WellnessToolkitAvailableConfig => {
  const baseConfig: WellnessToolkitAvailableConfig = {
    shouldNotBeTeen: false,
    shouldNotBeInternational: false,
    shouldEssentialsBeEnabled: false,
    shouldCoachingBeEnabled: false,
    shouldCoachingForParentsBeEnabled: false,
    shouldDependentsBeSupported: false,
    shouldNotBeSHOrSI: false,
    shouldHaveProviderSupply: false,
    shouldNotBeInActiveEpisode: false,
    shouldNotHaveT3Recommendation: false,
    shouldFeatureFlagBeEnabled: false,
    shouldDisplayLanguageBeEnglish: false,
    shouldRelatedSelfCareTopicNotBeDisabled: false,
    shouldHaveEligibleManagerCoachingSeat: false,
    shouldTherapyBeEnabled: false,
    shouldRequiredProgramsBeEnabled: false,
    shouldEligibilityDataBeLoaded: false,
  }

  const baseIndividualCoachingConfig: WellnessToolkitAvailableConfig = {
    ...baseConfig,
    shouldNotBeTeen: true,
    shouldEssentialsBeEnabled: true,
    shouldCoachingBeEnabled: true,
    shouldFeatureFlagBeEnabled: true,
    shouldDisplayLanguageBeEnglish: !isInternationalLifestyleCoachingEnabled,
    configExtras: {
      programSearchRequestData: COACHING_PARAMS,
    },
  }
  const defaultWFMHConfig: Partial<Record<TOOLKIT_TYPE, WellnessToolkitAvailableConfig>> = {
    [WELLNESS_TOOLKIT_PROMOTION.WORK]: baseIndividualCoachingConfig,
    [WELLNESS_TOOLKIT_PROMOTION.PURPOSE]: baseIndividualCoachingConfig,
    [WELLNESS_TOOLKIT_PROMOTION.RELATIONSHIPS]: baseIndividualCoachingConfig,
    [WELLNESS_TOOLKIT_PROMOTION.PARENT]: {
      ...baseConfig,
      shouldEssentialsBeEnabled: true,
      shouldNotBeTeen: true,
      shouldNotBeInternational: true,
      shouldCoachingForParentsBeEnabled: true,
      shouldNotBeSHOrSI: true,
      shouldHaveProviderSupply: true,
      shouldNotHaveT3Recommendation: true,
      shouldFeatureFlagBeEnabled: true,
      shouldDisplayLanguageBeEnglish: true,
      configExtras: {
        programSearchRequestData: PARENTING_PARAMS,
        triggerConditionsChecker: checkTriggerConditionsParent,
        allowedProgramsForActiveEpisodes: [ProgramNames.BlendedCareTherapy],
      },
    },
    [WELLNESS_TOOLKIT_PROMOTION.FAMILY]: {
      ...baseConfig,
      shouldNotBeTeen: true,
      shouldNotBeInternational: true,
      shouldEssentialsBeEnabled: true,
      shouldDependentsBeSupported: true,
      shouldNotBeSHOrSI: true,
      shouldFeatureFlagBeEnabled: true,
      configExtras: {
        triggerConditionsChecker: checkTriggerConditionsFamily,
      },
    },
    [WELLNESS_TOOLKIT_PROMOTION.TRAUMA]: {
      ...baseConfig,
      shouldFeatureFlagBeEnabled: true,
      shouldTherapyBeEnabled: true,
      configExtras: {
        triggerConditionsChecker: checkTriggerConditionsTrauma,
      },
    },
    [CENTER_OF_EXCELLENCE.NEURODIVERSITY]: {
      ...baseConfig,
      shouldFeatureFlagBeEnabled: true,
      shouldTherapyBeEnabled: true,
      configExtras: {
        triggerConditionsChecker: checkTriggerConditionsNeurodiversity,
      },
    },
    [CENTER_OF_EXCELLENCE.HEALTH_CHALLENGES]: {
      ...baseConfig,
      shouldNotBeTeen: true,
      shouldFeatureFlagBeEnabled: true,
      shouldTherapyBeEnabled: true,
      configExtras: {
        triggerConditionsChecker: checkTriggerConditionsHealthChallenges,
      },
    },
    [CENTER_OF_EXCELLENCE.WOMENS_HEALTH]: {
      ...baseConfig,
      shouldNotBeTeen: true,
      shouldFeatureFlagBeEnabled: true,
      shouldTherapyBeEnabled: true,
      configExtras: {
        triggerConditionsChecker: checkTriggerConditionsWomensHealth,
      },
    },
    [CENTER_OF_EXCELLENCE.MANAGER_HUB]: {
      ...baseConfig,
      shouldNotBeTeen: true,
      shouldFeatureFlagBeEnabled: true,
      shouldDisplayLanguageBeEnglish: true,
      anyOf: [
        // Basic Manager Hub
        { shouldEssentialsBeEnabled: true, shouldRelatedSelfCareTopicNotBeDisabled: true },
        // Manager Coaching
        { shouldNotBeInternational: true, shouldHaveEligibleManagerCoachingSeat: true },
      ],
      configExtras: {
        triggerConditionsChecker: checkTriggerConditionsManagerHub,
      },
    },
    /**
     * Pediatrics is an entry-point-only toolkit (see `ENTRYPOINT_ONLY_TOOLKITS`): this decides whether its
     * four entry points may show, not whether a toolkit tile is promoted. It deliberately inherits none of
     * the coaching-supply, Library, language, episode or risk gates — CEA-4012's rule is the three
     * pediatric programs, the launch flag, a US viewer, and the viewer's own age being 18 or over
     * (`shouldNotBeTeen`, the same 18+ check Manager Hub uses).
     */
    [CENTER_OF_EXCELLENCE.PEDIATRICS]: {
      ...baseConfig,
      shouldFeatureFlagBeEnabled: true,
      shouldRequiredProgramsBeEnabled: true,
      shouldNotBeInternational: true,
      shouldNotBeTeen: true,
      shouldEligibilityDataBeLoaded: true,
    },
  }

  const lyraSelectBaseConfig: WellnessToolkitAvailableConfig = {
    ...baseConfig,
    shouldCoachingBeEnabled: true,
  }

  const defaultSelectConfig: Partial<Record<TOOLKIT_TYPE, WellnessToolkitAvailableConfig>> = {
    [WELLNESS_TOOLKIT_PROMOTION.WORK]: lyraSelectBaseConfig,
    [WELLNESS_TOOLKIT_PROMOTION.PURPOSE]: lyraSelectBaseConfig,
    [WELLNESS_TOOLKIT_PROMOTION.RELATIONSHIPS]: lyraSelectBaseConfig,
    [WELLNESS_TOOLKIT_PROMOTION.PARENT]: { ...lyraSelectBaseConfig, shouldNotBeTeen: true },
    [WELLNESS_TOOLKIT_PROMOTION.STRESS]: lyraSelectBaseConfig,
    [WELLNESS_TOOLKIT_PROMOTION.WELLBEING]: lyraSelectBaseConfig,
    [WELLNESS_TOOLKIT_PROMOTION.MINDFULNESS]: lyraSelectBaseConfig,
    [WELLNESS_TOOLKIT_PROMOTION.SELF_COMPASSION]: lyraSelectBaseConfig,
    [WELLNESS_TOOLKIT_PROMOTION.LIFE_TRANSITIONS]: lyraSelectBaseConfig,
    [WELLNESS_TOOLKIT_PROMOTION.CAREGIVER_STRESS]: { ...lyraSelectBaseConfig, shouldNotBeTeen: true },
    [WELLNESS_TOOLKIT_PROMOTION.WORKING_WITH_OTHERS]: lyraSelectBaseConfig,
    [WELLNESS_TOOLKIT_PROMOTION.TRAUMA]: {
      ...lyraSelectBaseConfig,
      shouldFeatureFlagBeEnabled: true,
      shouldTherapyBeEnabled: true,
    },
  }

  /** Variations on the conditions depending on where the toolkit entry point is being shown */
  const wfmhConfigByPage: {
    [key in COMMON_MIXPANEL_PAGE]?: Partial<Record<TOOLKIT_TYPE, Partial<WellnessToolkitAvailableConfig>>>
  } = {
    [COMMON_MIXPANEL_PAGE.FORKED_TRIAGE]: {
      [WELLNESS_TOOLKIT_PROMOTION.PARENT]: {
        shouldEssentialsBeEnabled: false,
      },
    },
    [COMMON_MIXPANEL_PAGE.INTEGRATED_BENEFITS_PAGE]: {
      [WELLNESS_TOOLKIT_PROMOTION.FAMILY]: {
        /** Essentials carousel on the family hub page will be hidden */
        shouldEssentialsBeEnabled: false,
        shouldNotBeSHOrSI: false,
      },
    },
  }

  const wfmhPageConfigForToolkits = page && wfmhConfigByPage[page]

  const wfmhConfigResult = {
    ...defaultWFMHConfig[toolkit],
    ...(wfmhPageConfigForToolkits && wfmhPageConfigForToolkits[toolkit]),
  } as WellnessToolkitAvailableConfig

  const selectConfigByPage: {
    [key in COMMON_MIXPANEL_PAGE]?: Partial<Record<TOOLKIT_TYPE, Partial<WellnessToolkitAvailableConfig>>>
  } = {
    [COMMON_MIXPANEL_PAGE.INTEGRATED_BENEFITS_PAGE]: {
      [WELLNESS_TOOLKIT_PROMOTION.TRAUMA]: {
        shouldTherapyBeEnabled: false,
      },
    },
  }
  const selectPageConfigForToolkits = page && selectConfigByPage[page]

  // If toolkit is not explicitly defined for Select, force hide it to prevent WFMH-only toolkits from being available
  const isToolkitDefinedForSelect = defaultSelectConfig[toolkit] !== undefined
  const selectConfigResult = isToolkitDefinedForSelect
    ? ({
        ...defaultSelectConfig[toolkit],
        ...(selectPageConfigForToolkits && selectPageConfigForToolkits[toolkit]),
      } as WellnessToolkitAvailableConfig)
    : ({ ...baseConfig, configExtras: { forceHideToolkit: true } } as WellnessToolkitAvailableConfig)

  return isLyraSelect ? selectConfigResult : wfmhConfigResult
}

const evaluateShortCircuitConditions = ({
  toolkitLabel,
  conditions,
  isFeatureFlagEnabled,
  isEssentialsEnabled,
  isCoachingEnabled,
  isTherapyEnabled,
  isCoachingForParentsEnabled,
  hideChildSearchCTA,
  hideDependentField,
  isUserTeen,
  isInternationalUser,
  isUserDisplayLanguageEnglish,
  disableSelfCareTopics,
  managerCoachingSeatEligibility,
  areRequiredProgramsEnabled,
  isEligibilityDataLoaded,
}: Omit<ToolkitShortCircuitEligibilityInputs, 'conditions'> & {
  conditions: Partial<ToolkitShortCircuitConditions>
}): ShortCircuitEligibilityResult => {
  const reasons: ToolkitAvailabilityReason[] = []

  const recordAvailabilityCondition = (name: string, value?: boolean | number) => {
    const propertyName = `${toolkitLabel}-${name}`
    const existing = reasons.find((item) => item.propertyName === propertyName)
    if (existing) existing.value = value
    else reasons.push({ propertyName, value })
  }

  const {
    shouldFeatureFlagBeEnabled,
    shouldEssentialsBeEnabled,
    shouldCoachingBeEnabled,
    shouldTherapyBeEnabled,
    shouldCoachingForParentsBeEnabled,
    shouldDependentsBeSupported,
    shouldNotBeTeen,
    shouldNotBeInternational,
    shouldDisplayLanguageBeEnglish,
    shouldRelatedSelfCareTopicNotBeDisabled,
    shouldHaveEligibleManagerCoachingSeat,
    shouldRequiredProgramsBeEnabled,
    shouldEligibilityDataBeLoaded,
  } = conditions

  if (shouldFeatureFlagBeEnabled) {
    recordAvailabilityCondition('isFeatureFlagEnabled', isFeatureFlagEnabled)
  }
  if (shouldEssentialsBeEnabled) {
    recordAvailabilityCondition('isEssentialsEnabled', isEssentialsEnabled)
  }
  if (shouldCoachingBeEnabled) {
    recordAvailabilityCondition('isCoachingEnabled', isCoachingEnabled)
  }
  if (shouldTherapyBeEnabled) {
    recordAvailabilityCondition('isTherapyEnabled', isTherapyEnabled)
  }
  if (shouldCoachingForParentsBeEnabled) {
    recordAvailabilityCondition('isCoachingForParentsEnabled', isCoachingForParentsEnabled)
  }
  if (shouldDependentsBeSupported) {
    const value = !hideChildSearchCTA && !hideDependentField
    recordAvailabilityCondition('!hideDependentField && !hideChildSearchCTA', value)
  }
  if (shouldNotBeTeen) {
    recordAvailabilityCondition('!isUserTeen', !isUserTeen)
  }
  if (shouldNotBeInternational) {
    recordAvailabilityCondition('!isInternationalUser', !isInternationalUser)
  }
  if (shouldRequiredProgramsBeEnabled) {
    recordAvailabilityCondition('areRequiredProgramsEnabled', areRequiredProgramsEnabled)
  }
  if (shouldEligibilityDataBeLoaded) {
    recordAvailabilityCondition('isEligibilityDataLoaded', isEligibilityDataLoaded)
  }
  if (shouldDisplayLanguageBeEnglish) {
    recordAvailabilityCondition('isUserDisplayLanguageEnglish', isUserDisplayLanguageEnglish)
  }
  const hasEligibleManagerCoachingSeat =
    managerCoachingSeatEligibility?.status === MANAGER_COACHING_SEAT_ELIGIBILITY_STATUS.ELIGIBLE &&
    getManagerCoachingRemainingSessions(managerCoachingSeatEligibility.license) !== 0
  if (shouldHaveEligibleManagerCoachingSeat) {
    recordAvailabilityCondition('hasEligibleManagerCoachingSeat', hasEligibleManagerCoachingSeat)
  }

  const relatedSelfCareTopicId = TOOLKIT_TO_LIBRARY_TOPIC_ID[toolkitLabel as TOOLKIT_TYPE]
  const isRelatedSelfCareTopicDisabled =
    !!relatedSelfCareTopicId &&
    (disableSelfCareTopics?.split(';').some((topicId) => topicId.trim() === relatedSelfCareTopicId) ?? false)

  if (shouldRelatedSelfCareTopicNotBeDisabled) {
    recordAvailabilityCondition('!isRelatedSelfCareTopicDisabled', !isRelatedSelfCareTopicDisabled)
  }

  /** Evaluate the higher level conditions so we can short circuit the logic that is more expensive, like speculative search */
  const hasPassedShortCircuitEligibilityChecks =
    (shouldFeatureFlagBeEnabled ? !!isFeatureFlagEnabled : true) &&
    (shouldEssentialsBeEnabled ? isEssentialsEnabled : true) &&
    (shouldCoachingBeEnabled ? isCoachingEnabled : true) &&
    (shouldTherapyBeEnabled ? isTherapyEnabled : true) &&
    (shouldCoachingForParentsBeEnabled ? isCoachingForParentsEnabled : true) &&
    (shouldDependentsBeSupported ? !hideChildSearchCTA && !hideDependentField : true) &&
    (shouldNotBeTeen ? !isUserTeen : true) &&
    (shouldNotBeInternational ? !isInternationalUser : true) &&
    (shouldRequiredProgramsBeEnabled ? !!areRequiredProgramsEnabled : true) &&
    (shouldEligibilityDataBeLoaded ? !!isEligibilityDataLoaded : true) &&
    (shouldHaveEligibleManagerCoachingSeat ? hasEligibleManagerCoachingSeat : true) &&
    (shouldDisplayLanguageBeEnglish ? isUserDisplayLanguageEnglish : true) &&
    (shouldRelatedSelfCareTopicNotBeDisabled ? !isRelatedSelfCareTopicDisabled : true)

  return { hasPassedShortCircuitEligibilityChecks, reasons }
}

export const evaluateShortCircuitEligibility = (
  inputs: ToolkitShortCircuitEligibilityInputs,
): ShortCircuitEligibilityResult => {
  const { conditions, configExtras, toolkitLabel } = inputs
  const commonResult = evaluateShortCircuitConditions(inputs)
  const hasPassedAnyOfConditions =
    conditions.anyOf?.some(
      (group) =>
        evaluateShortCircuitConditions({ ...inputs, conditions: group }).hasPassedShortCircuitEligibilityChecks,
    ) ?? true
  const isNotForceHidden = !(configExtras?.forceHideToolkit ?? false)
  const reasons = [
    { propertyName: `${toolkitLabel}-!forceHideToolkit`, value: isNotForceHidden },
    ...commonResult.reasons,
  ]
  if (conditions.anyOf) {
    reasons.push({ propertyName: `${toolkitLabel}-anyOf`, value: hasPassedAnyOfConditions })
  }

  return {
    hasPassedShortCircuitEligibilityChecks:
      isNotForceHidden && commonResult.hasPassedShortCircuitEligibilityChecks && hasPassedAnyOfConditions,
    reasons,
  }
}

export const evaluateToolkitAvailability = (inputs: ToolkitAvailabilityInputs): ToolkitAvailabilityResult => {
  const { hasPassedShortCircuitEligibilityChecks, reasons } = evaluateShortCircuitEligibility(inputs)

  const {
    conditions,
    isNotSHOrSI,
    isNotInActiveEpisode,
    isNotT3Recommendation,
    shouldHaveProviderSupply,
    hasAvailableProviderSupply,
    toolkitLabel,
  } = inputs

  const recordAvailabilityCondition = (name: string, value?: boolean | number) => {
    const propertyName = `${toolkitLabel}-${name}`
    const existing = reasons.find((item) => item.propertyName === propertyName)
    if (existing) {
      existing.value = value
    } else {
      reasons.push({ propertyName, value })
    }
  }

  if (conditions.shouldNotBeSHOrSI) {
    recordAvailabilityCondition('isNotSHOrSI', isNotSHOrSI)
  }
  if (conditions.shouldNotBeInActiveEpisode) {
    recordAvailabilityCondition('isNotInActiveEpisode', isNotInActiveEpisode)
  }
  if (conditions.shouldNotHaveT3Recommendation) {
    recordAvailabilityCondition('isNotT3Recommendation', isNotT3Recommendation)
  }
  if (conditions.shouldHaveProviderSupply) {
    recordAvailabilityCondition('hasAvailableProviderSupply', hasAvailableProviderSupply)
  }

  const isToolkitAvailable =
    hasPassedShortCircuitEligibilityChecks &&
    (conditions.shouldNotBeSHOrSI ? isNotSHOrSI : true) &&
    (conditions.shouldNotBeInActiveEpisode ? isNotInActiveEpisode : true) &&
    (conditions.shouldNotHaveT3Recommendation ? isNotT3Recommendation : true) &&
    (shouldHaveProviderSupply ? hasAvailableProviderSupply : true)

  return { hasPassedShortCircuitEligibilityChecks, isToolkitAvailable, reasons }
}

export const isNotSelfHarmOrSuicialIdeationIndicated = (risksFactors?: RiskFactorsResponse) => {
  // @todo Check for SI indicated in assessments
  return risksFactors ? !hasPastSI(risksFactors) : true
}

/**
 *  Verify if users triage searches is safe for wellness toolkit.
 *  If the latest completed triage that is not Tier 3, return true
 *
 * @param searches
 */
export const isLatestCompletedSearchNotTierThree = (searches: TriageSearch[]) => {
  const filteredSearches = searches.filter((s) => !isChildSearch(s))
  let latestCompletedSearch: TriageSearch | undefined = undefined

  for (let i = filteredSearches.length - 1; i >= 0; i--) {
    const search = filteredSearches[i]
    if (search.triage_status === TriageSearchStatus.COMPLETED) {
      latestCompletedSearch = search
      break
    }
  }
  return (
    filteredSearches.length === 0 ||
    !latestCompletedSearch ||
    latestCompletedSearch?.recommended_treatments?.shownTierPage !== TREATMENT_RECOMMENDATION_TIER.TIER_3
  )
}

/**
 * Checks that user isn't in an active episode
 * - Excludes dependent episodes
 * @param allowedPrograms - An episode in one of these programs is exempt. SELF_CARE_APPS program is always allowed.
 */
export const isNotInActiveEpisode = (
  episodes: Episode[],
  lyraId?: string,
  allowedPrograms: ProgramNames[] = [],
): boolean => {
  const programs = [...allowedPrograms, ProgramNames.SELF_CARE_APPS]
  const filteredEpisodes = getEpisodesForSelf(episodes, lyraId)
  return !filteredEpisodes.some(
    ({ program_name, state }) => !programs.includes(program_name) && state === EpisodeStates.IN_PROGRESS,
  )
}

const getEpisodesForSelf = (episodes: Episode[], lyraId?: string) => {
  if (!lyraId) {
    return episodes
  }
  const filtered = episodes.filter((e) => e.patient_id === lyraId)
  // Fallback: if no episodes explicitly match the user's lyraId, assume the list already
  // represents the user's own episodes (e.g., when fixtures omit/mismatch patient_id).
  if (filtered.length === 0 && episodes.length > 0) {
    console.warn('getEpisodesForSelf: No episodes matched lyraId, returning all episodes as fallback')
  }
  return filtered.length > 0 ? filtered : episodes
}

export const getWellnessToolkitCardMetadataForWellnessGoal = (
  wellnessGoal: WELLNESS_GOALS_WORD_CLOUD_OPTIONS,
  isLyraSelect: boolean,
) => {
  const toolkitPromotion = WELLNESS_GOAL_TO_WELLNESS_TOOLKIT_PROMOTION[wellnessGoal]
  return {
    id: toolkitPromotion,
    title: getToolkitCardTitle(toolkitPromotion, isLyraSelect),
    accessibilityLabel: getWellnessToolkitCardAccessibilityLabel(toolkitPromotion),
    ranking: WELLNESS_GOALS_RANKING[wellnessGoal],
  }
}

const isEligibleForWellnessGoal = (goal: WELLNESS_GOALS_WORD_CLOUD_OPTIONS, isUserMinor?: boolean) => {
  if (
    goal === WELLNESS_GOALS_WORD_CLOUD_OPTIONS.CAREGIVER_SUPPORT ||
    goal === WELLNESS_GOALS_WORD_CLOUD_OPTIONS.PARENTING_SUPPORT
  ) {
    return !isUserMinor
  }
  return true
}

export const getEligibleWellnessGoals = (isUserMinor?: boolean) => {
  return WELLNESS_GOALS_WORD_CLOUD_ORDER.filter((key) => isEligibleForWellnessGoal(key, isUserMinor))
}

const getDefaultWellnessGoals = (isUserMinor?: boolean) => {
  // In the default case these three goals are always shown in this order (i.e. the ranking defined in WELLNESS_GOALS_RANKING is not considered)
  const firstThreeGoals: WELLNESS_GOALS_WORD_CLOUD_OPTIONS[] = [
    WELLNESS_GOALS_WORD_CLOUD_OPTIONS.IMPROVE_YOUR_WELLBEING,
    WELLNESS_GOALS_WORD_CLOUD_OPTIONS.MANAGE_STRESS,
    WELLNESS_GOALS_WORD_CLOUD_OPTIONS.MINDFULNESS,
  ]

  // Sort remaining goals by their ranking defined in WELLNESS_GOALS_RANKING
  const remainingGoals: WELLNESS_GOALS_WORD_CLOUD_OPTIONS[] = getEligibleWellnessGoals(isUserMinor)
    .filter((key) => !firstThreeGoals.includes(key as WELLNESS_GOALS_WORD_CLOUD_OPTIONS))
    .sort((a, b) => {
      return (
        WELLNESS_GOALS_RANKING[a as WELLNESS_GOALS_WORD_CLOUD_OPTIONS] -
        WELLNESS_GOALS_RANKING[b as WELLNESS_GOALS_WORD_CLOUD_OPTIONS]
      )
    })

  return [...firstThreeGoals, ...remainingGoals]
}

export const getDefaultWellnessToolkits = (isLyraSelect: boolean, isUserMinor?: boolean) => {
  return getDefaultWellnessGoals(isUserMinor).map((key) =>
    getWellnessToolkitCardMetadataForWellnessGoal(key as WELLNESS_GOALS_WORD_CLOUD_OPTIONS, isLyraSelect),
  )
}

export const getSortedWellnessGoals = (selectedGoals: WELLNESS_GOALS_WORD_CLOUD_OPTIONS[], isUserMinor?: boolean) => {
  // The first three default goals that have priority (in this specific order) when less than 3 goals are selected
  const defaultFirstThreeGoals: WELLNESS_GOALS_WORD_CLOUD_OPTIONS[] = [
    WELLNESS_GOALS_WORD_CLOUD_OPTIONS.IMPROVE_YOUR_WELLBEING,
    WELLNESS_GOALS_WORD_CLOUD_OPTIONS.MANAGE_STRESS,
    WELLNESS_GOALS_WORD_CLOUD_OPTIONS.MINDFULNESS,
  ]

  const allGoals: WELLNESS_GOALS_WORD_CLOUD_OPTIONS[] = getEligibleWellnessGoals(isUserMinor)

  const sortRemainingGoals = (alreadySortedGoals: WELLNESS_GOALS_WORD_CLOUD_OPTIONS[]) => {
    return allGoals
      .filter((goal) => !alreadySortedGoals.includes(goal))
      .sort((a, b) => {
        return WELLNESS_GOALS_RANKING[a] - WELLNESS_GOALS_RANKING[b]
      })
  }

  switch (selectedGoals.length) {
    case 0: {
      return getDefaultWellnessGoals(isUserMinor)
    }
    case 1: {
      // append the single selected goal to the front of the list, remove any duplicates if it's one of the default goals and then take the first three
      const sortedFirstThreeGoals = uniq([...selectedGoals, ...defaultFirstThreeGoals]).slice(0, 3)

      const remainingGoals = sortRemainingGoals(sortedFirstThreeGoals)
      return [...sortedFirstThreeGoals, ...remainingGoals]
    }
    case 2: {
      // sort the two selected goals by ranking defined in WELLNESS_GOALS_RANKING
      const sortedSelectedGoals = [...selectedGoals].sort((a, b) => {
        return WELLNESS_GOALS_RANKING[a] - WELLNESS_GOALS_RANKING[b]
      })

      // Combine sorted selected goals with default goals, remove duplicates, and keep only the top 3.
      // This ensures the third spot is filled with the highest priority default goal not already selected.
      const sortedFirstThreeGoals = uniq([...sortedSelectedGoals, ...defaultFirstThreeGoals]).slice(0, 3)

      const remainingGoals = sortRemainingGoals(sortedFirstThreeGoals)

      return [...sortedFirstThreeGoals, ...remainingGoals]
    }
    default: {
      const sortedSelectedGoals = [...selectedGoals].sort((a, b) => {
        return WELLNESS_GOALS_RANKING[a] - WELLNESS_GOALS_RANKING[b]
      })

      const remainingGoals = sortRemainingGoals(sortedSelectedGoals)
      return [...sortedSelectedGoals, ...remainingGoals]
    }
  }
}

const getRankingSystem = (productType: PRODUCT_TYPE, customerName?: string, isTriggered?: boolean) => {
  if (productType === PRODUCT_TYPE.WFMH && customerName) {
    if (isTriggered) {
      return WFMH_TRIGGERED_TOOLKIT_RANKING
    }
    return customerName.toLowerCase() === CustomerName.WALMART
      ? WFMH_DEFAULT_TOOLKIT_PROMOTION_RANKING
      : WFMH_DEPRIORITIZED_NEURODIVERSITY_COE_PROMOTION_RANKING // Deprioritizing neurodiversity for all other WFMH customers due to limited neurodiversity provider supply
  }
  return
}

export const sortToolkitsByRank = ({
  productType,
  toolkits,
  customerName,
  isTriggered,
}: {
  productType: PRODUCT_TYPE
  toolkits: TOOLKIT_TYPE[]
  customerName?: string
  isTriggered?: boolean
}) => {
  const rankingSystem: Partial<Record<TOOLKIT_TYPE, number>> | undefined = getRankingSystem(
    productType,
    customerName,
    isTriggered,
  )

  if (rankingSystem) {
    return sortBy(toolkits, (toolkit) => rankingSystem![toolkit] ?? Number.MAX_SAFE_INTEGER)
  } else {
    return toolkits
  }
}
